/**
 * LanguageSuggestionChip.jsx — the soft "seems you might prefer X — switch?"
 * chip Rammy surfaces beneath her reply when the user just wrote in a language
 * that differs from their profile setting.
 *
 * Design principles baked in:
 * - Never blocks the reply — it's an OFFER, not a demand. Ignoring it should
 *   be as friction-free as accepting it.
 * - One-tap switch — no confirmation modal, no "are you sure". Language is
 *   easy to change back in Settings; we don't paternalise.
 * - Sticky dismiss — "No thanks" persists forever (or until profile changes)
 *   so we never nag a user who just happened to type one Hindi word.
 * - Warm cyan-lavender palette matching Sprint C's CheckinCard so the chip
 *   reads as "Rammy noticing something" rather than "system asking for input".
 */
import { useState } from "react";
import * as L from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export function LanguageSuggestionChip({ code, name, onSwitched, onDismissed, testid }) {
  const [state, setState] = useState("idle"); // idle | switching | switched | dismissed
  const busy = state === "switching";

  const accept = async () => {
    if (busy) return;
    setState("switching");
    try {
      await api.patch("/me/settings", { language: code });
      setState("switched");
      toast.success(`Rammy will reply in ${name} from now on`);
      onSwitched?.();
    } catch (e) {
      setState("idle");
      toast.error("Couldn't switch — try again in a sec");
    }
  };

  const dismiss = async () => {
    if (busy) return;
    setState("dismissed");
    try {
      await api.post("/me/language-suggestion/dismiss", { code });
      onDismissed?.();
    } catch (_) {
      // Silent — dismissal is a nicety. If it fails, next turn will still
      // detect the mismatch, and the user can just dismiss again.
    }
  };

  // Once accepted OR dismissed, drop the chip entirely — no lingering
  // "switched" pill; the parent's next-turn re-render will use the fresh
  // language and the chip won't reappear.
  if (state === "switched" || state === "dismissed") return null;

  return (
    <div
      className="mt-2 inline-flex items-center gap-2 rounded-2xl border border-[var(--rammy-cyan)]/30 bg-gradient-to-br from-[var(--rammy-cyan)]/10 via-white to-[var(--rammy-cyan)]/5 px-3 py-2 flex-wrap"
      data-testid={testid}
    >
      <L.Languages size={13} className="text-[var(--rammy-cyan-dark)] shrink-0" />
      <span className="text-[12px] text-[var(--rammy-text)] leading-snug">
        Seems you might prefer <strong className="font-semibold">{name}</strong> — switch?
      </span>
      <div className="flex items-center gap-1 ml-auto">
        <button
          type="button"
          onClick={accept}
          disabled={busy}
          data-testid={`${testid}-accept`}
          className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-[var(--rammy-cyan-dark)] text-white text-[11.5px] font-semibold hover:opacity-90 transition disabled:opacity-70"
        >
          {busy ? <L.Loader2 size={11} className="animate-spin"/> : <L.Check size={11}/>}
          Switch
        </button>
        <button
          type="button"
          onClick={dismiss}
          disabled={busy}
          data-testid={`${testid}-dismiss`}
          className="inline-flex items-center h-7 px-3 rounded-full text-[11.5px] text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)] transition"
        >
          No thanks
        </button>
      </div>
    </div>
  );
}
